import type { Translations } from '../../i18n/translations'

type Mode = 'signIn' | 'signUp' | 'magicLink' | 'forgotPassword' | 'resetPassword'
type EntryMode = 'auth' | 'checkAlbums'
type InfoPage = 'home' | 'howto' | 'about' | 'contact' | 'terms'

interface AuthPanelProps {
  t: Translations
  isDarkMode: boolean
  mode: Mode
  busy: boolean
  email: string
  password: string
  confirmPassword: string
  error: string | null
  notice: string | null
  showAuthForm: boolean
  entryMode: EntryMode
  infoPage: InfoPage
  setInfoPage: (page: InfoPage) => void
  maxEmailLength: number
  maxPasswordLength: number
  setEmail: (value: string) => void
  setPassword: (value: string) => void
  setConfirmPassword: (value: string) => void
  changeMode: (mode: Mode) => void
  openAuth: (mode: Mode) => void
  onCheckAlbums?: () => void
  onSubmit: (event: React.FormEvent) => Promise<void>
}

export function AuthPanel({
  t,
  isDarkMode,
  mode,
  busy,
  email,
  password,
  confirmPassword,
  error,
  notice,
  showAuthForm,
  entryMode,
  infoPage,
  setInfoPage,
  maxEmailLength,
  maxPasswordLength,
  setEmail,
  setPassword,
  setConfirmPassword,
  changeMode,
  openAuth,
  onCheckAlbums,
  onSubmit
}: AuthPanelProps): JSX.Element {
  const templateTheme = isDarkMode ? 'dark' : 'light'
  const logoSrc = `/template-assets/${templateTheme}/logo-transparent.png`

  if (!showAuthForm) {
    return (
      <div className="site-topbar" aria-label={t.publicPagesLabel}>
        <img className="site-topbar-logo" src={logoSrc} alt={t.appName} />

        <nav className="site-topbar-nav" aria-label={t.publicPagesLabel}>
          <button
            type="button"
            className={`site-topbar-link ${infoPage === 'home' ? 'is-active' : ''}`}
            onClick={() => setInfoPage('home')}
          >
            {t.publicHome}
          </button>
          <button
            type="button"
            className={`site-topbar-link ${infoPage === 'howto' ? 'is-active' : ''}`}
            onClick={() => setInfoPage('howto')}
          >
            {t.publicHowTo}
          </button>
          <button
            type="button"
            className={`site-topbar-link ${infoPage === 'about' ? 'is-active' : ''}`}
            onClick={() => setInfoPage('about')}
          >
            {t.publicAbout}
          </button>
          <button type="button" className="site-topbar-link" disabled>
            Recursos
          </button>
          <button type="button" className="site-topbar-link" disabled>
            Educacao
          </button>
          <button
            type="button"
            className={`site-topbar-link ${infoPage === 'contact' ? 'is-active' : ''}`}
            onClick={() => setInfoPage('contact')}
          >
            {t.publicContact}
          </button>
          <button
            type="button"
            className={`site-topbar-link ${infoPage === 'terms' ? 'is-active' : ''}`}
            onClick={() => setInfoPage('terms')}
          >
            {t.publicTerms}
          </button>
        </nav>

        <div className="auth-cta-actions">
          {entryMode === 'checkAlbums' ? (
            <button type="button" className="auth-submit" onClick={onCheckAlbums}>
              {t.checkYourAlbums}
            </button>
          ) : (
            <button type="button" className="auth-submit" onClick={() => openAuth('signUp')}>
              {t.authSignUp}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <form className="auth-card" onSubmit={(event) => void onSubmit(event)}>
      <h1 className="auth-title">{t.appName}</h1>
      <p className="auth-subtitle">{t.authSubtitle}</p>

      <label className="auth-field">
        <span>{t.authEmail}</span>
        <input
          type="email"
          autoComplete="email"
          required
          maxLength={maxEmailLength}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>

      {mode !== 'magicLink' && mode !== 'forgotPassword' && (
        <label className="auth-field">
          <span>{t.authPassword}</span>
          <input
            type="password"
            autoComplete={
              mode === 'signUp' || mode === 'resetPassword' ? 'new-password' : 'current-password'
            }
            required
            minLength={8}
            maxLength={maxPasswordLength}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
      )}

      {mode === 'resetPassword' && (
        <label className="auth-field">
          <span>{t.authConfirmPassword}</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={maxPasswordLength}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </label>
      )}

      {mode === 'signIn' && (
        <div className="auth-inline-actions">
          <button type="button" className="auth-link" onClick={() => changeMode('forgotPassword')}>
            {t.authForgotPassword}
          </button>
        </div>
      )}

      {error && (
        <p className="auth-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="auth-notice" role="status">
          {notice}
        </p>
      )}

      <button className="auth-submit" type="submit" disabled={busy}>
        {mode === 'signIn' && t.authSignIn}
        {mode === 'signUp' && t.authSignUp}
        {mode === 'magicLink' && t.authSendMagicLink}
        {mode === 'forgotPassword' && t.authSendPasswordReset}
        {mode === 'resetPassword' && t.authUpdatePassword}
      </button>

      <div className="auth-switch">
        {mode !== 'signIn' && (
          <button type="button" className="auth-link" onClick={() => changeMode('signIn')}>
            {t.authSignIn}
          </button>
        )}
        {mode !== 'signUp' && mode !== 'resetPassword' && (
          <button type="button" className="auth-link" onClick={() => changeMode('signUp')}>
            {t.authSignUp}
          </button>
        )}
        {mode !== 'magicLink' && mode !== 'resetPassword' && (
          <button type="button" className="auth-link" onClick={() => changeMode('magicLink')}>
            {t.authMagicLink}
          </button>
        )}
        {mode !== 'forgotPassword' && mode !== 'resetPassword' && (
          <button type="button" className="auth-link" onClick={() => changeMode('forgotPassword')}>
            {t.authForgotPassword}
          </button>
        )}
      </div>
    </form>
  )
}
